import {
  type ReactNode,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react'

import { useMemoizedFn } from 'ahooks'
import { createStyles } from 'antd-style'
import { noop } from 'lodash'

import type { TableCellPanelProps } from './interface'

const useStyles = createStyles(({ token }) => ({
  panel: {
    padding: token.padding,
    borderRadius: token.borderRadius,
    backgroundColor: token.colorWhite
  },
  title: {
    paddingBlockEnd: token.padding,
    fontSize: token.fontSize,
    color: token.colorText,
    textAlign: 'center'
  },
  container: {
    display: 'grid',
    gap: token.paddingXS
  },
  cell: {
    height: 16,
    width: 16,
    border: `1px solid ${token.colorBorder}`,
    borderRadius: token.borderRadiusXS
  },
  activeCell: {
    borderColor: token.colorPrimaryBorderHover
  }
}))

export const TableCellPanel = ({
  ref,
  title,
  cols,
  rows,
  onSelect = noop
}: TableCellPanelProps) => {
  const isClickCellRef = useRef(false)
  const [activeCell, setActiveCell] = useState({
    row: 0,
    col: 0
  })

  useImperativeHandle(ref, () => ({
    reset() {
      setActiveCell({
        row: 0,
        col: 0
      })
    }
  }))

  const { cx, styles } = useStyles()
  const handleSelect = useMemoizedFn(onSelect)

  const cells = useMemo(() => {
    const arr: ReactNode[] = []
    for (let i = 1; i <= rows; i++) {
      for (let j = 1; j <= cols; j++) {
        arr.push(
          <span
            key={`${i}_${j}`}
            role='button'
            tabIndex={-1}
            className={cx(styles.cell, {
              [styles.activeCell]: i <= activeCell.row && j <= activeCell.col
            })}
            onClick={() => {
              isClickCellRef.current = true
              handleSelect(i, j)
            }}
            onPointerOver={() => {
              setActiveCell({
                row: i,
                col: j
              })
            }}
          />
        )
      }
    }
    return arr
  }, [
    cx,
    styles.activeCell,
    styles.cell,
    cols,
    rows,
    activeCell.col,
    activeCell.row,
    handleSelect
  ])

  const titleText =
    activeCell.col && activeCell.row
      ? `${activeCell.col} x ${activeCell.row}`
      : title

  return (
    <div
      className={styles.panel}
      role='button'
      tabIndex={0}
      onClick={e => {
        if (!isClickCellRef.current) {
          e.stopPropagation()
        }
        isClickCellRef.current = false
      }}
    >
      <div className={styles.title}>{titleText}</div>
      <div
        className={styles.container}
        style={{
          gridTemplateColumns: `repeat(${cols},1fr)`,
          gridTemplateRows: `repeat(${rows},1fr)`
        }}
      >
        {cells}
      </div>
    </div>
  )
}
